import type { QueryFunctionContext, QueryKey } from '@tanstack/react-query';

import { useInfiniteQueryWithCursor } from '../../../ReactQuery/index';
import { get } from '../../../redux/api';
import type { OIDCTrustRelationship } from '../../../../shared/model-typings/oidctrustrelationship';
import type { CursorPaginatedResponse } from '../../../../shared/types/routes/pagination';

type TrustRelationshipsFilter = {
  userId?: number;
  providerId?: number;
};

/**
 * Fetch OIDC trust relationships with cursor pagination.
 * Can filter by userId or providerId.
 */
function fetchTrustRelationships(
  { pageParam }: QueryFunctionContext<QueryKey, number | undefined>,
  filter: TrustRelationshipsFilter,
  pageSize: number,
) {
  const cursor = pageParam ?? 0;
  return get<CursorPaginatedResponse<OIDCTrustRelationship, number>>(
    `/api/oidc/trust-relationships`,
    false,
    {
      pageSize,
      prev: cursor,
      ...filter,
    },
  );
}

export const TRUST_RELATIONSHIPS_QUERY_KEY = 'oidc-trust-relationships';

const getQueryKey = (filter: TrustRelationshipsFilter, pageSize: number): QueryKey =>
  [TRUST_RELATIONSHIPS_QUERY_KEY, filter, pageSize];

/**
 * Hook to fetch OIDC trust relationships with cursor-based infinite pagination.
 * Pass userId or providerId to filter results.
 */
export default function useTrustRelationships(
  filter: TrustRelationshipsFilter,
  pageSize: number = 50,
) {
  return useInfiniteQueryWithCursor<CursorPaginatedResponse<OIDCTrustRelationship, number>>({
    queryKey: getQueryKey(filter, pageSize),
    queryFn: (context) => fetchTrustRelationships(context, filter, pageSize),
    staleTime: 5 * 60 * 1000,
  });
}
